import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  Box,
  Typography,
  Button,
  Stack,
  CircularProgress,
  Alert,
  Chip,
  alpha,
  Collapse,
} from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import { SidebarContext } from "../Sidebar/SidebarProvider";
import { getUserById } from "../../api/User/userApi";
import { mapUserToPermissionState } from "../../utils/userPermissionsUtils";
import { useProveedorPermissions } from "../../hooks/useProveedorPermissions";
import {
  loadDashboardLayout,
  saveDashboardLayout,
  clearDashboardLayout,
  loadDashboardLayoutLocal,
} from "./dashboardLayoutStorage";
import {
  getDefaultDashboardLayout,
  createWidgetFromCatalog,
} from "./defaultLayouts";
import DashboardCanvas from "./DashboardCanvas";
import WidgetCatalogDrawer, { DRAWER_WIDTH } from "./WidgetCatalogDrawer";
import {
  dashPageBgSx,
  dashHeroSx,
  dashToolbarBtnSx,
  dashEmptySx,
} from "./myDashboardStyles";

const MotionBox = motion(Box);

function formatToday(locale) {
  try {
    return new Intl.DateTimeFormat(locale || undefined, {
      weekday: "long",
      day: "numeric",
      month: "long",
    }).format(new Date());
  } catch {
    return "";
  }
}

export default function MyDashboardPage() {
  const { t, i18n } = useTranslation();
  const { contentMargin } = useContext(SidebarContext);
  const { loading: proveedorLoading, hasProveedorLimit } =
    useProveedorPermissions();

  const [userId, setUserId] = useState(null);
  const [userName, setUserName] = useState("");
  const [permissions, setPermissions] = useState({});
  const [loading, setLoading] = useState(true);
  const [layoutLoading, setLayoutLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [howtoOpen, setHowtoOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [layouts, setLayouts] = useState({ lg: [] });
  const [dirty, setDirty] = useState(false);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  const todayLabel = useMemo(
    () => formatToday(i18n.language),
    [i18n.language],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        if (!token) {
          setLoading(false);
          return;
        }
        const decoded = jwtDecode(token);
        const uid = decoded.userId;
        setUserId(uid);
        setUserName(decoded.username || decoded.name || decoded.email || "");
        const user = await getUserById(uid);
        if (cancelled) return;
        const resolvedName =
          user?.username ||
          user?.Nombre ||
          user?.name ||
          decoded.username ||
          decoded.name ||
          decoded.email ||
          "";
        setUserName(resolvedName);
        setPermissions(mapUserToPermissionState(user || {}));
      } catch (err) {
        console.error("MyDashboard load user:", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const applyLayout = useCallback((layout) => {
    setWidgets(layout.widgets || []);
    setLayouts(layout.layouts || { lg: [] });
    setDirty(false);
  }, []);

  useEffect(() => {
    if (loading || proveedorLoading || !userId) return;
    let cancelled = false;

    (async () => {
      setLayoutLoading(true);
      try {
        const saved = await loadDashboardLayout(userId);
        if (cancelled) return;
        if (saved) {
          applyLayout(saved);
          setSavedSnapshot(saved);
        } else {
          const defaults = getDefaultDashboardLayout({
            hasProveedorLimit,
            permissions,
          });
          applyLayout(defaults);
          setSavedSnapshot(null);
        }
      } finally {
        if (!cancelled) setLayoutLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    loading,
    proveedorLoading,
    userId,
    hasProveedorLimit,
    permissions,
    applyLayout,
  ]);

  const placedDataCatalogIds = useMemo(
    () => widgets.filter((w) => w.type === "data").map((w) => w.catalogId),
    [widgets],
  );

  const handleLayoutChange = (nextLg) => {
    setLayouts({ lg: nextLg });
    setDirty(true);
  };

  const handleRemove = (instanceId) => {
    setWidgets((prev) => prev.filter((w) => w.i !== instanceId));
    setLayouts((prev) => ({
      lg: (prev.lg || []).filter((l) => l.i !== instanceId),
    }));
    setDirty(true);
  };

  const handleAdd = (catalogItem) => {
    if (
      catalogItem.type === "data" &&
      widgets.some((w) => w.catalogId === catalogItem.id)
    ) {
      return;
    }
    const { widget, layout } = createWidgetFromCatalog(
      catalogItem,
      layouts.lg || [],
    );
    setWidgets((prev) => [...prev, widget]);
    setLayouts((prev) => ({ lg: [...(prev.lg || []), layout] }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (!userId || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      const saved = await saveDashboardLayout(userId, { widgets, layouts });
      setSavedSnapshot(saved);
      setDirty(false);
      setEditing(false);
      setCatalogOpen(false);
    } catch (err) {
      console.error(err);
      setSaveError(t("myDashboard.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!userId || saving) return;
    setSaving(true);
    setSaveError(null);
    try {
      await clearDashboardLayout(userId);
      const defaults = getDefaultDashboardLayout({
        hasProveedorLimit,
        permissions,
      });
      applyLayout(defaults);
      setSavedSnapshot(null);
      setEditing(false);
      setCatalogOpen(false);
    } catch (err) {
      console.error(err);
      setSaveError(t("myDashboard.saveError"));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEdit = () => {
    if (editing) {
      const fallback =
        savedSnapshot ||
        loadDashboardLayoutLocal(userId) ||
        getDefaultDashboardLayout({ hasProveedorLimit, permissions });
      applyLayout(fallback);
      setEditing(false);
      setCatalogOpen(false);
      setSaveError(null);
      return;
    }
    setEditing(true);
    setCatalogOpen(true);
  };

  const howToSteps = editing
    ? [
        t("myDashboard.howToStepEdit1"),
        t("myDashboard.howToStepEdit2"),
        t("myDashboard.howToStepEdit3"),
        t("myDashboard.howToStepEdit4"),
      ]
    : [
        t("myDashboard.howToStep1"),
        t("myDashboard.howToStep2"),
        t("myDashboard.howToStep3"),
        t("myDashboard.howToStep4"),
      ];

  if (loading || proveedorLoading || layoutLoading) {
    return (
      <Box
        sx={[
          contentMargin,
          dashPageBgSx,
          {
            minHeight: "60vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        ]}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={[
        contentMargin,
        dashPageBgSx,
        {
          width: "auto",
          maxWidth: "100%",
          boxSizing: "border-box",
          p: { xs: 1.5, sm: 2, md: 2.5 },
          pb: 5,
          pr: catalogOpen
            ? { xs: 1.5, sm: 2, md: `${DRAWER_WIDTH + 24}px` }
            : { xs: 1.5, sm: 2, md: 2.5 },
          transition: "padding-right 0.25s ease",
        },
      ]}
    >
      <MotionBox
        sx={dashHeroSx}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            px: { xs: 2, sm: 2.75 },
            py: { xs: 2, sm: 2.5 },
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ minWidth: 0, flex: "1 1 220px" }}>
            {todayLabel && (
              <Typography
                variant="overline"
                sx={{
                  display: "block",
                  fontWeight: 700,
                  letterSpacing: 1.1,
                  color: "text.secondary",
                  mb: 0.5,
                  textTransform: "capitalize",
                }}
              >
                {todayLabel}
              </Typography>
            )}
            <Typography
              variant="h4"
              sx={{
                fontWeight: 800,
                letterSpacing: -0.4,
                lineHeight: 1.15,
                fontSize: { xs: "1.55rem", sm: "1.85rem" },
              }}
            >
              {userName ? (
                <>
                  <Box
                    component="span"
                    sx={{
                      color: "text.secondary",
                      fontWeight: 600,
                      mr: 0.75,
                    }}
                  >
                    {t("myDashboard.hello")}
                  </Box>
                  <Box
                    component="span"
                    sx={(theme) => ({
                      background:
                        theme.palette.mode === "dark"
                          ? `linear-gradient(120deg, ${theme.palette.primary.light}, #4dd0e1)`
                          : `linear-gradient(120deg, ${theme.palette.primary.main}, #00a8b8)`,
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    })}
                  >
                    {userName}
                  </Box>
                </>
              ) : (
                t("myDashboard.title")
              )}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.75, maxWidth: 420, lineHeight: 1.45 }}
            >
              {t("myDashboard.subtitle")}
            </Typography>
          </Box>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {editing && (
              <Chip
                size="small"
                label={t("myDashboard.editing")}
                sx={(theme) => ({
                  fontWeight: 700,
                  bgcolor: alpha(theme.palette.primary.main, 0.12),
                  color: "primary.main",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.25)}`,
                })}
              />
            )}
            {editing && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<WidgetsOutlinedIcon />}
                onClick={() => setCatalogOpen(true)}
                sx={dashToolbarBtnSx}
              >
                {t("myDashboard.catalog")}
              </Button>
            )}
            <Button
              size="small"
              variant={howtoOpen ? "contained" : "outlined"}
              color={howtoOpen ? "info" : "inherit"}
              startIcon={
                howtoOpen ? <ExpandLessIcon /> : <HelpOutlineOutlinedIcon />
              }
              onClick={() => setHowtoOpen((v) => !v)}
              sx={dashToolbarBtnSx}
            >
              {t("myDashboard.howToShow")}
            </Button>
            <Button
              size="small"
              variant={editing ? "contained" : "outlined"}
              disabled={saving}
              startIcon={editing ? <SaveOutlinedIcon /> : <EditOutlinedIcon />}
              onClick={editing ? handleSave : handleToggleEdit}
              sx={(theme) =>
                dashToolbarBtnSx(theme, { primary: editing })
              }
            >
              {editing
                ? saving
                  ? t("myDashboard.saving")
                  : dirty
                    ? t("myDashboard.save")
                    : t("myDashboard.done")
                : t("myDashboard.edit")}
            </Button>
            {editing && (
              <Button
                size="small"
                color="inherit"
                disabled={saving}
                startIcon={<RestartAltOutlinedIcon />}
                onClick={handleReset}
                sx={dashToolbarBtnSx}
              >
                {t("myDashboard.reset")}
              </Button>
            )}
            {editing && (
              <Button
                size="small"
                disabled={saving}
                onClick={handleToggleEdit}
                sx={dashToolbarBtnSx}
              >
                {t("myDashboard.cancel")}
              </Button>
            )}
          </Stack>
        </Box>
      </MotionBox>

      {saveError && (
        <Alert severity="error" sx={{ mb: 2, borderRadius: 2.5 }} onClose={() => setSaveError(null)}>
          {saveError}
        </Alert>
      )}

      <Collapse in={howtoOpen} unmountOnExit>
        <Alert
          severity="info"
          icon={<HelpOutlineOutlinedIcon fontSize="inherit" />}
          onClose={() => setHowtoOpen(false)}
          sx={(theme) => ({
            mb: 2,
            borderRadius: 2.5,
            alignItems: "flex-start",
            border: `1px solid ${alpha(theme.palette.info.main, theme.palette.mode === "dark" ? 0.4 : 0.28)}`,
            bgcolor: alpha(
              theme.palette.info.main,
              theme.palette.mode === "dark" ? 0.14 : 0.07,
            ),
            color: "text.primary",
            "& .MuiAlert-icon": {
              color: theme.palette.info.main,
            },
            "& .MuiAlert-message": { width: "100%" },
          })}
        >
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.75 }}>
            {editing
              ? t("myDashboard.howToTitleEditing")
              : t("myDashboard.howToTitle")}
          </Typography>
          <Box
            component="ol"
            sx={{
              m: 0,
              pl: 2.25,
              "& li": {
                mb: 0.35,
                fontSize: "0.875rem",
                lineHeight: 1.45,
                color: "text.secondary",
              },
            }}
          >
            {howToSteps.map((step) => (
              <li key={step}>{step}</li>
            ))}
          </Box>
        </Alert>
      </Collapse>

      {!widgets.length && (
        <Box sx={(theme) => dashEmptySx(theme)}>
          <Typography variant="subtitle1" fontWeight={800} gutterBottom>
            {t("myDashboard.emptyTitle")}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t("myDashboard.empty")}
          </Typography>
          <Button
            variant="contained"
            startIcon={<EditOutlinedIcon />}
            onClick={handleToggleEdit}
            sx={(theme) => dashToolbarBtnSx(theme, { primary: true })}
          >
            {t("myDashboard.edit")}
          </Button>
        </Box>
      )}

      <Box sx={{ width: "100%", maxWidth: "100%" }}>
        <DashboardCanvas
          widgets={widgets}
          layouts={layouts}
          editing={editing}
          onLayoutChange={handleLayoutChange}
          onRemove={handleRemove}
        />
      </Box>

      <WidgetCatalogDrawer
        open={catalogOpen && editing}
        onClose={() => setCatalogOpen(false)}
        permissions={permissions}
        hasProveedorLimit={hasProveedorLimit}
        placedCatalogIds={placedDataCatalogIds}
        onAdd={handleAdd}
      />
    </Box>
  );
}
